import React, { useState, useEffect, useCallback } from 'react';
import PanelLayout from '../../Panel/PanelLayout';
import { adminGatewayAPI } from '../../../services/api';
import { Zap, Users2, RefreshCw, AlertCircle } from 'lucide-react';

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

// ── Gateway / Partner logo chip ───────────────────────────────────────────────
const GatewayLogo = ({ name, color }) => {
  const labels = {
    payu: 'PayU', paytm: 'Paytm', razorpay: 'RPay',
    phonepe: 'PhPe', cashfree: 'CFree', hdfc: 'HDFC',
    cashfree_partner: 'CF✦', phonepe_partner: 'PP✦',
  };
  return (
    <div style={{
      width: 48, height: 48, borderRadius: 14,
      background: color + '18', border: `2px solid ${color}30`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: 12, color, letterSpacing: -0.5, flexShrink: 0,
    }}>
      {labels[name] || name.toUpperCase().slice(0, 4)}
    </div>
  );
};

// ── Badge ─────────────────────────────────────────────────────────────────────
const Badge = ({ children, color, bg }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    background: bg, color,
  }}>
    {children}
  </span>
);

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ msg }) => {
  if (!msg) return null;
  const isErr = msg.startsWith('Error');
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 2000,
      background: isErr ? '#FEF2F2' : '#F0FDF4',
      border: `1.5px solid ${isErr ? '#FCA5A5' : '#86EFAC'}`,
      color: isErr ? '#DC2626' : '#15803D',
      padding: '14px 20px', borderRadius: 10,
      fontSize: 14, fontWeight: 500, maxWidth: 400,
      boxShadow: '0 4px 16px rgba(0,0,0,.1)',
    }}>
      {msg}
    </div>
  );
};

// ── Confirmation modal ────────────────────────────────────────────────────────
const ConfirmModal = ({ item, isPartner, onConfirm, onCancel, loading }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  }}>
    <div style={{
      background: '#fff', borderRadius: 16, padding: '32px 28px',
      maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>{isPartner ? '🤝' : '⚠️'}</div>
      <h3 style={{ margin: '0 0 10px', fontSize: 18, color: '#14142b' }}>
        Switch to {item?.label}?
      </h3>
      <p style={{ margin: '0 0 6px', color: '#4a4a6a', fontSize: 14, lineHeight: 1.6 }}>
        {isPartner
          ? <>All new payments will be routed through <strong>A1xPay's {item?.label} partner account</strong>. Merchants don't need to change anything.</>
          : <>All <strong>new payment requests</strong> from all merchants will immediately route through <strong>{item?.label}</strong>.</>
        }
      </p>
      <p style={{ margin: '0 0 24px', color: '#4a4a6a', fontSize: 13 }}>
        In-progress transactions will complete on their current gateway. Merchants will not be notified.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel} disabled={loading}
          style={{
            padding: '10px 20px', border: '1.5px solid #e8e8f1',
            borderRadius: 8, background: '#fff', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, color: '#4a4a6a',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm} disabled={loading}
          style={{
            padding: '10px 22px', border: 'none', borderRadius: 8,
            background: item?.color || '#14142b',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: 14, fontWeight: 600, color: '#fff', opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Switching…' : `Yes, switch to ${item?.label}`}
        </button>
      </div>
    </div>
  </div>
);

// ── Gateway / Partner row (list pattern, no card) ────────────────────────────
const GatewayRow = ({ gw, isPartner, onSwitch }) => {
  const isActive     = gw.is_active;
  const isConfigured = gw.is_configured;
  const accentColor  = gw.color || '#6e6e86';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '16px 20px',
      borderBottom: `1px solid ${C.borderSoft}`,
      borderLeft: isActive ? `3px solid ${accentColor}` : '3px solid transparent',
      background: isActive ? accentColor + '06' : 'transparent',
      transition: 'background .15s',
    }}>
      {/* Logo */}
      <GatewayLogo name={gw.name} color={accentColor} />

      {/* Name + desc */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: C.ink }}>{gw.label}</span>
          {isPartner && <Badge color={C.primary} bg={C.primarySoft}>PARTNER</Badge>}
          {isActive && (
            <Badge color={accentColor} bg={accentColor + '18'}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, display: 'inline-block' }} />
              ACTIVE
            </Badge>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {gw.description}
        </div>
      </div>

      {/* Config status */}
      <div style={{ textAlign: 'center', flexShrink: 0, display: 'none' }} className="gw-txn">
        <div style={{ fontWeight: 700, fontSize: 15, color: C.ink }}>{gw.txn_7d ?? '—'}</div>
        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>Txns 7d</div>
      </div>

      {/* Configured pill */}
      <div style={{ flexShrink: 0 }}>
        {isConfigured
          ? <span style={{ fontSize: 11, fontWeight: 700, color: C.success, background: C.successSoft, padding: '3px 10px', borderRadius: 20 }}>✓ Ready</span>
          : <span style={{ fontSize: 11, fontWeight: 700, color: C.danger,  background: C.dangerSoft,  padding: '3px 10px', borderRadius: 20 }}>✗ Not Set</span>
        }
      </div>

      {/* Action */}
      <div style={{ flexShrink: 0 }}>
        {isActive ? (
          <span style={{ fontSize: 12, fontWeight: 600, color: accentColor, padding: '8px 16px', borderRadius: 8, background: accentColor + '12', display: 'inline-block' }}>
            Active
          </span>
        ) : (
          <button
            onClick={() => onSwitch(gw)}
            disabled={!isConfigured}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 8,
              background: isConfigured ? accentColor : C.borderSoft,
              color: isConfigured ? '#fff' : C.muted,
              cursor: isConfigured ? 'pointer' : 'not-allowed',
              fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap',
            }}
            title={!isConfigured ? 'Set credentials in .env first' : ''}
          >
            Switch
          </button>
        )}
      </div>
    </div>
  );
};

// ── Tab button ────────────────────────────────────────────────────────────────
const TabBtn = ({ label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    style={{
      padding: '10px 22px', border: 'none', cursor: 'pointer',
      fontWeight: 600, fontSize: 14, borderRadius: 8,
      background: active ? '#14142b' : 'transparent',
      color: active ? '#fff' : '#4a4a6a',
      transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 8,
    }}
  >
    {label}
    {badge != null && (
      <span style={{
        background: active ? '#ffffff30' : '#e8e8f1',
        color: active ? '#fff' : '#4a4a6a',
        padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      }}>
        {badge}
      </span>
    )}
  </button>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const AdminGatewayManage = () => {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [confirm, setConfirm]     = useState(null);   // {item, isPartner}
  const [switching, setSwitching] = useState(false);
  const [toast, setToast]         = useState('');
  const [tab, setTab]             = useState('gateways'); // 'gateways' | 'partners'

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 5000); };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await adminGatewayAPI.getGateways();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load gateway information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSwitch = async () => {
    if (!confirm) return;
    setSwitching(true);
    try {
      const res = await adminGatewayAPI.setActive(confirm.item.name);
      showToast(res.data.message || `Switched to ${confirm.item.label}`);
      setConfirm(null);
      await load();
    } catch (err) {
      showToast('Error: ' + (err.response?.data?.message || 'Switch failed'));
    } finally {
      setSwitching(false);
    }
  };

  const partners = data?.partners || [];
  const gateways = data?.gateways || [];

  return (
    <PanelLayout role="admin" title="Gateway Manage" breadcrumb="Admin / Gateway Manage">

      <Toast msg={toast} />

      {confirm && (
        <ConfirmModal
          item={confirm.item}
          isPartner={confirm.isPartner}
          onConfirm={handleSwitch}
          onCancel={() => setConfirm(null)}
          loading={switching}
        />
      )}

      <div style={{ maxWidth: 1100, margin: '0 auto', minWidth: 0, overflow: 'hidden' }}>

        {/* Page header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: C.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Zap size={18} color={C.primary} strokeWidth={2} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: '-.02em' }}>Payment Gateway Management</h1>
              <p style={{ margin: 0, fontSize: 13, color: C.muted }}>Switch the active gateway or partner for all merchants in one click.</p>
            </div>
          </div>
        </div>

        {/* Active banners */}
        {data && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em' }}>Active Gateway</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.success, flexShrink: 0 }} />
                <div style={{ fontWeight: 800, fontSize: 18, color: C.ink, textTransform: 'uppercase' }}>{data.active_gateway || '—'}</div>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 200, background: C.primarySoft, border: `1.5px solid ${C.primary}30`, borderRadius: C.radius, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '.07em', opacity: .7 }}>Active Partner</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: data.active_partner ? C.primary : C.muted, flexShrink: 0 }} />
                <div style={{ fontWeight: 800, fontSize: 18, color: C.primary, textTransform: 'uppercase' }}>{data.active_partner || 'None'}</div>
              </div>
            </div>
            {data.last_changed_at && (
              <div style={{ alignSelf: 'center', color: C.muted, fontSize: 12 }}>
                Last changed: {new Date(data.last_changed_at).toLocaleString('en-IN')}
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: 4, width: 'fit-content' }}>
          {[
            { key: 'gateways', label: 'Payment Gateways', Icon: Zap, count: gateways.length },
            { key: 'partners', label: 'Partner Integrations', Icon: Users2, count: partners.length },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ height: 34, padding: '0 16px', borderRadius: 6, border: 'none', background: tab === t.key ? C.primary : 'transparent', color: tab === t.key ? '#fff' : C.muted, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit', transition: 'all .15s' }}>
              <t.Icon size={13} /> {t.label}
              {t.count > 0 && <span style={{ background: tab === t.key ? 'rgba(255,255,255,0.25)' : C.borderSoft, color: tab === t.key ? '#fff' : C.muted, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999 }}>{t.count}</span>}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 60, color: '#6e6e86' }}>
            Loading gateway information…
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{
            background: '#FEF2F2', border: '1.5px solid #FCA5A5',
            borderRadius: 10, padding: '14px 18px', color: '#DC2626', marginBottom: 24,
          }}>
            {error}
            <button
              onClick={load}
              style={{ marginLeft: 12, background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ── GATEWAYS TAB ─────────────────────────────────────────────────── */}
        {!loading && !error && data && tab === 'gateways' && (
          <>
            <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden', marginBottom: 20 }}>
              {gateways.length === 0
                ? <div style={{ padding: '40px 20px', textAlign: 'center', color: C.muted, fontSize: 13 }}>No gateways found.</div>
                : gateways.map((gw) => (
                    <GatewayRow key={gw.name} gw={gw} isPartner={false} onSwitch={(item) => setConfirm({ item, isPartner: false })} />
                  ))
              }
            </div>
            <InfoBox isPartner={false} />
          </>
        )}

        {/* ── PARTNERS TAB ─────────────────────────────────────────────────── */}
        {!loading && !error && data && tab === 'partners' && (
          <>
            <div style={{ background: C.primarySoft, border: `1.5px solid ${C.primary}25`, borderRadius: C.radiusSm, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>🤝</span>
              <div style={{ fontSize: 13, color: C.primary, lineHeight: 1.6 }}>
                <strong>Partner / Aggregator Mode —</strong> A1xPay routes all payments via its single partner account. No per-merchant credentials needed.
              </div>
            </div>

            <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden', marginBottom: 20 }}>
              {partners.length === 0
                ? <div style={{ padding: '40px 20px', textAlign: 'center', color: C.muted, fontSize: 13 }}>No partner integrations found.</div>
                : partners.map((gw) => (
                    <GatewayRow key={gw.name} gw={gw} isPartner={true} onSwitch={(item) => setConfirm({ item, isPartner: true })} />
                  ))
              }
            </div>

            <div style={{ background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Required .env keys</div>
              <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#00B95F', marginBottom: 6 }}>Cashfree Partner</div>
                  {['CASHFREE_PARTNER_API_KEY', 'CASHFREE_PARTNER_WEBHOOK_SECRET', 'CASHFREE_PARTNER_ENV', 'CASHFREE_PARTNER_RETURN_URL'].map(k => (
                    <div key={k} style={{ fontFamily: 'Menlo, monospace', fontSize: 11, color: C.body, marginBottom: 3 }}>{k}</div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#5F259F', marginBottom: 6 }}>PhonePe Partner</div>
                  {['PHONEPE_PARTNER_CLIENT_ID', 'PHONEPE_PARTNER_CLIENT_SECRET', 'PHONEPE_PARTNER_CLIENT_VERSION', 'PHONEPE_PARTNER_ENV', 'PHONEPE_PARTNER_REDIRECT_URL'].map(k => (
                    <div key={k} style={{ fontFamily: 'Menlo, monospace', fontSize: 11, color: C.body, marginBottom: 3 }}>{k}</div>
                  ))}
                </div>
              </div>
            </div>

            <InfoBox isPartner={true} />
          </>
        )}
      </div>
    </PanelLayout>
  );
};

// ── Info box ──────────────────────────────────────────────────────────────────
const InfoBox = ({ isPartner }) => (
  <div style={{
    marginTop: 20, background: '#f9f9fc', borderRadius: 10,
    border: '1.5px solid #e8e8f1', padding: '16px 20px',
  }}>
    <div style={{ fontWeight: 600, fontSize: 13, color: '#14142b', marginBottom: 8 }}>
      {isPartner ? 'How Partner mode works' : 'How gateway switching works'}
    </div>
    <ul style={{ margin: 0, paddingLeft: 18, color: '#4a4a6a', fontSize: 13, lineHeight: 1.8 }}>
      {isPartner ? (
        <>
          <li>A1xPay's partner API key routes payments for all sub-merchants under one account.</li>
          <li>Each merchant needs to be onboarded on the partner portal — their sub-merchant ID is stored in A1xPay DB.</li>
          <li>Switch is <strong>instant</strong> — next payment request uses the partner gateway.</li>
          <li>Merchants don't need to change their API calls — routing is transparent.</li>
          <li>Partner credentials must be set in server <code>.env</code> before switching.</li>
        </>
      ) : (
        <>
          <li>The switch is <strong>instant</strong> — next payment request uses the new gateway.</li>
          <li>In-progress transactions complete on their original gateway; no disruption.</li>
          <li>Merchants make no changes on their end — their API calls remain identical.</li>
          <li>Webhooks from the previous gateway will still be processed normally.</li>
          <li>Gateway credentials must be set in server <code>.env</code> before switching.</li>
        </>
      )}
    </ul>
  </div>
);

export default AdminGatewayManage;
